import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Heading,
  Icon,
  Icons,
  Input,
  Label,
  ListBox,
  Loading,
  Popover,
  Select,
  SelectItem,
  SelectValue,
  Text,
  TextField,
  Checkbox,
  DialogTrigger,
} from "@phoenix/components";
import { SelectChevronUpDownIcon } from "@phoenix/components/core/icon";
import { useNotifyError, useNotifySuccess } from "@phoenix/contexts";

import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";

const pageContainer = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-150);
  padding: var(--global-dimension-size-150);
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`;

const breadcrumbRow = css`
  display: flex;
  gap: var(--global-dimension-size-75);
  align-items: center;
  flex-wrap: wrap;
`;

const breadcrumbLink = css`
  font-size: 0.875rem;
  color: var(--color-text-500);
  text-decoration: none;
  &:hover {
    color: var(--color-text-900);
    text-decoration: underline;
  }
`;

const headerSection = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-25);
  margin-bottom: var(--global-dimension-size-50);
`;

const configurationCard = css`
  background: var(--global-color-gray-75);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 20px;
  padding: var(--global-dimension-size-150);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.03);
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-150);
  box-sizing: border-box;
`;

const formRow = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--global-dimension-size-150);
  width: 100%;
  box-sizing: border-box;
`;

const fieldWrapper = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-50);
  width: 100%;

  .react-aria-Select, .react-aria-TextField {
    display: flex;
    flex-direction: column;
    gap: var(--global-dimension-size-50);
    width: 100%;
  }

  button {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;
    box-sizing: border-box;
  }

  input {
    width: 100%;
    box-sizing: border-box;
  }
`;

const requiredStar = css`
  color: var(--color-danger-900, #ef4444);
  margin-left: 2px;
`;

const labelStyle = css`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-800);
`;

const sectionHeader = css`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-text-900);
`;

const evaluatorsTitle = css`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-text-800);
`;

const divider = css`
  height: 1px;
  background-color: var(--color-border-default, #e2e8f0);
  border: none;
  margin: 0;
`;

const evaluatorsContainer = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-75);
`;

const tagContainer = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--global-dimension-size-50);
`;

const greenTag = css`
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.08);
  color: #059669;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid rgba(16, 185, 129, 0.16);
  transition: all 150ms ease;
  user-select: none;

  &:hover {
    background: rgba(16, 185, 129, 0.12);
    border-color: rgba(16, 185, 129, 0.24);
  }
`;

const actionsRow = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--global-dimension-size-100);
  margin-top: var(--global-dimension-size-50);
`;

const selectedProjectsContainer = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--global-color-gray-75, #ffffff);
  border: 1px solid var(--ac-global-color-grey-200, rgba(15, 23, 42, 0.08));
  border-radius: var(--ac-global-rounding-medium, 12px);
  padding: var(--global-dimension-size-100, 16px);
  margin-top: var(--global-dimension-size-75, 12px);
  width: 100%;
  box-sizing: border-box;
`;

const selectedProjectsHeader = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const selectedProjectsTitle = css`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-text-800, #1e293b);
`;

const trashIconStyle = css`
  width: 14px;
  height: 14px;
  color: currentColor;
`;

const folderIconStyle = css`
  width: 14px;
  height: 14px;
  color: currentColor;
  opacity: 0.8;
`;

const badgesWrapper = css`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const badgeItemStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.875rem;
  font-weight: 500;
  background: rgba(13, 148, 136, 0.05); /* light teal background */
  color: #0d9488; /* teal text */
  border: 1px solid rgba(13, 148, 136, 0.18);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(13, 148, 136, 0.1);
    border-color: rgba(13, 148, 136, 0.25);
  }
`;

const badgeRemoveButtonStyle = css`
  background: transparent;
  border: none;
  color: rgba(13, 148, 136, 0.6);
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(13, 148, 136, 0.15);
    color: #0d9488;
  }
`;

const clearAllButtonStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: var(--ac-global-color-blue-600, #2563eb);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  width: auto;
  justify-content: center;

  &:hover {
    background: rgba(37, 99, 235, 0.06);
    text-decoration: underline;
  }
`;

export function LlmAssistedEvaluationPage() {
  const navigate = useNavigate();
  const notifyError = useNotifyError();
  const notifySuccess = useNotifySuccess();

  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [datasets, setDatasets] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedProjectId, setSelectedProjectId] = useState<string>("__all__");
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [experimentName, setExperimentName] = useState("");
  const [running, setRunning] = useState(false);

  const handleProjectChange = (key: any) => {
    const selectedKey = key ? String(key) : "__all__";
    if (selectedKey === "__all__") {
      setSelectedProjectId("__all__");
      return;
    }

    const currentSelected = selectedProjectId === "__all__" ? [] : selectedProjectId.split(",").filter(Boolean);
    let updated: string[];
    if (currentSelected.includes(selectedKey)) {
      updated = currentSelected.filter((p) => p !== selectedKey);
    } else {
      updated = [...currentSelected, selectedKey];
    }

    const newSelection = updated.length > 0 ? updated.join(",") : "__all__";
    setSelectedProjectId(newSelection);
  };

  useEffect(() => {
    let active = true;

    async function loadData() {
      try {
        const [projRes, dataRes] = await Promise.all([
          authFetch(prependBasename("/v1/projects")),
          authFetch(prependBasename("/v1/datasets?limit=100")),
        ]);

        if (!projRes.ok) throw new Error("Failed to load projects");
        if (!dataRes.ok) throw new Error("Failed to load datasets");

        const projData = await projRes.json();
        const dataData = await dataRes.json();

        if (active) {
          setProjects(projData.data || []);
          setDatasets(dataData.data || []);
        }
      } catch (err) {
        if (active) {
          notifyError({
            title: "Failed to load configuration options",
            message: err instanceof Error ? err.message : "Unknown error",
          });
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      active = false;
    };
  }, [notifyError]);

  const handleCancel = () => {
    navigate("/templates/dataset");
  };

  const handleRunEvaluation = () => {
    if (!selectedDatasetId) {
      notifyError({
        title: "Required fields missing",
        message: "Please select a Source Dataset.",
      });
      return;
    }

    setRunning(true);

    const isAllProjects = selectedProjectId === "__all__";
    const projName = isAllProjects ? "__all__" : selectedProjectId;
    const datasetName = datasets.find((d) => d.id === selectedDatasetId)?.name || "";
    const scopeLabel = isAllProjects ? "All Projects" : `projects "${projName}"`;

    authFetch(prependBasename("/v1/templates/run-dataset"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        template_id: "llm-assisted",
        project_name: projName,
        dataset_name: datasetName,
        experiment_name: experimentName || null,
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to start evaluation script on backend");
        }
        return res.json();
      })
      .then(() => {
        notifySuccess({
          title: "Evaluation started",
          message: `Automated Model Evaluation started successfully for dataset "${datasetName}" across ${scopeLabel}.`,
        });
        navigate("/templates/dataset");
      })
      .catch((err) => {
        notifyError({
          title: "Failed to run evaluation",
          message: err instanceof Error ? err.message : "Unknown error",
        });
      })
      .finally(() => {
        setRunning(false);
      });
  };

  return (
    <div css={pageContainer}>
      <div css={breadcrumbRow}>
        <a href={prependBasename("/templates")} css={breadcrumbLink}>
          Templates
        </a>
        <Text color="text-400">/</Text>
        <a href={prependBasename("/templates/dataset")} css={breadcrumbLink}>
          Dataset Templates
        </a>
        <Text color="text-400">/</Text>
        <Text color="text-500">Automated Model Evaluation</Text>
      </div>

      <div css={headerSection}>
        <Heading level={1}>Automated Model Evaluation</Heading>
        <Text color="text-500" size="M">
          Grade model outputs using a judge model to evaluate correctness, instruction-following, helpfulness, and reasoning.
        </Text>
      </div>

      {loading ? (
        <div css={css`min-height: 300px; display: grid; place-items: center;`}>
          <Loading />
        </div>
      ) : (
        <div css={configurationCard}>
          <Heading level={3} css={sectionHeader}>
            Configuration
          </Heading>

          <div css={formRow}>
            {/* Project Selection */}
            <div css={fieldWrapper}>
              <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                <Label css={labelStyle}>Project Name</Label>
                <DialogTrigger>
                  <Button style={{ width: "100%", justifyContent: "space-between" }}>
                    {selectedProjectId === "__all__" ? (
                      <span>🌐 All Projects</span>
                    ) : selectedProjectId.trim() ? (
                      selectedProjectId.split(",").filter(Boolean).length > 1 ? (
                        <span>{selectedProjectId.split(",").filter(Boolean).length} Projects Selected</span>
                      ) : (
                        <span>{selectedProjectId}</span>
                      )
                    ) : (
                      <span>Select Projects</span>
                    )}
                    <SelectChevronUpDownIcon />
                  </Button>
                  <Popover placement="bottom start">
                    <div
                      style={{
                        background: "var(--global-background-color-default, var(--spectrum-alias-background-color-default, #fff))",
                        border: "1px solid var(--global-border-color-default, var(--global-color-gray-300, rgba(15, 23, 42, 0.15)))",
                        color: "var(--global-text-color-default)",
                        borderRadius: "8px",
                        boxShadow: "var(--ac-premium-shadow-elevated, 0 4px 12px rgba(0, 0, 0, 0.08))",
                        padding: "8px",
                        minWidth: "240px",
                        maxHeight: "320px",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <div
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid var(--global-color-gray-200, rgba(15, 23, 42, 0.08))",
                          marginBottom: "4px",
                        }}
                      >
                        <Checkbox
                          isSelected={selectedProjectId === "__all__"}
                          onChange={(selected) => {
                            if (selected) {
                              setSelectedProjectId("__all__");
                            } else {
                              setSelectedProjectId("");
                            }
                          }}
                        >
                          🌐 All Projects
                        </Checkbox>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {projects.map((p) => {
                          const isAllSelected = selectedProjectId === "__all__";
                          const isSelected = isAllSelected || selectedProjectId.split(",").filter(Boolean).includes(p.name);
                          return (
                            <div key={p.name} style={{ padding: "4px 8px" }}>
                              <Checkbox
                                isSelected={isSelected}
                                onChange={(selected) => {
                                  let newSelection = "";
                                  let currentSelected = isAllSelected
                                    ? projects.map((proj) => proj.name)
                                    : selectedProjectId.split(",").filter(Boolean);

                                  if (selected) {
                                    if (!currentSelected.includes(p.name)) {
                                      currentSelected.push(p.name);
                                    }
                                  } else {
                                    currentSelected = currentSelected.filter((name) => name !== p.name);
                                  }

                                  if (currentSelected.length === 0) {
                                    newSelection = "";
                                  } else if (currentSelected.length === projects.length) {
                                    newSelection = "__all__";
                                  } else {
                                    newSelection = currentSelected.join(",");
                                  }

                                  setSelectedProjectId(newSelection);
                                }}
                              >
                                {p.name}
                              </Checkbox>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Popover>
                </DialogTrigger>
              </div>
            </div>

            {/* Dataset Selection */}
            <div css={fieldWrapper}>
              <Select
                aria-label="Source Dataset"
                value={selectedDatasetId || ""}
                onChange={(key) => setSelectedDatasetId(key ? String(key) : null)}
                isRequired
              >
                <Label css={labelStyle}>
                  Source Dataset<span css={requiredStar}>*</span>
                </Label>
                <Button>
                  <SelectValue>Select dataset</SelectValue>
                  <SelectChevronUpDownIcon />
                </Button>
                <Popover>
                  <ListBox>
                    {datasets.map((d) => (
                      <SelectItem key={d.id} id={d.id} textValue={d.name}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </ListBox>
                </Popover>
              </Select>
            </div>

            {/* Experiment Name */}
            <div css={fieldWrapper}>
              <TextField
                value={experimentName}
                onChange={setExperimentName}
                aria-label="Experiment Name"
              >
                <Label css={labelStyle}>Experiment Name (Optional)</Label>
                <Input placeholder="e.g., LLM_Judge_Eval_001" />
              </TextField>
            </div>
          </div>

          {/* Selected Projects container */}
          {selectedProjectId !== "__all__" && selectedProjectId.trim() !== "" && (
            <div css={selectedProjectsContainer}>
              <div css={selectedProjectsHeader}>
                <span css={selectedProjectsTitle}>
                  Selected Projects ({selectedProjectId.split(",").filter(Boolean).length})
                </span>
                <button
                  type="button"
                  css={clearAllButtonStyle}
                  onClick={() => setSelectedProjectId("__all__")}
                >
                  <Icon svg={<Icons.TrashOutline />} css={trashIconStyle} />
                  Clear all
                </button>
              </div>
              <div css={badgesWrapper}>
                {selectedProjectId.split(",").filter(Boolean).map((projName) => (
                  <span key={projName} css={badgeItemStyle}>
                    <Icon svg={<Icons.FolderOutline />} css={folderIconStyle} />
                    <span>{projName}</span>
                    <button
                      type="button"
                      css={badgeRemoveButtonStyle}
                      onClick={() => {
                        const currentSelected = selectedProjectId.split(",").filter(Boolean);
                        const updated = currentSelected.filter((p) => p !== projName);
                        const newSelection = updated.length > 0 ? updated.join(",") : "__all__";
                        setSelectedProjectId(newSelection);
                      }}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <hr css={divider} />

          {/* Evaluators Tag section */}
          <div css={evaluatorsContainer}>
            <Text css={evaluatorsTitle}>Evaluators</Text>
            <div css={tagContainer}>
              <span css={greenTag}>LLM Correctness</span>
              <span css={greenTag}>Relevance</span>
              <span css={greenTag}>Helpfulness</span>
              <span css={greenTag}>Instruction Following</span>
              <span css={greenTag}>Reasoning Quality</span>
              <span css={greenTag}>CoT Quality</span>
            </div>
          </div>

          <hr css={divider} />

          {/* Form Actions */}
          <div css={actionsRow}>
            <Button variant="default" size="M" onPress={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="M"
              leadingVisual={<Icon svg={<Icons.PlayCircleOutline />} />}
              onPress={handleRunEvaluation}
              isDisabled={running || !selectedDatasetId}
            >
              {running ? "Running..." : "Run Evaluation"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
